import { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata, usePermissions } from "Flocknote/apiHooks";

import { useTopGroups } from "GroupsManagement/hooks/useNavBarGroups";
import { LinkListRowsWrapper } from "NavBar/components/LinkListRow";
import NavLink from "NavBar/components/NavLink";
import NavLinksWrapper from "NavBar/components/NavLinksWrapper";
import NavSection from "NavBar/components/NavSection";
import NavSectionDivider from "NavBar/components/NavSectionDivider";
import {
  navSectionKeys,
  useEveryoneLinkRef,
  useGroupCommunicationRef,
  useGroupCommunicationSettingsRef,
} from "NavBar/navBarStore";
import {
  useIsEveryoneLinkFlashing,
  useIsGroupCommunicationSettingsFlashing,
  useIsGroupCommunicationSquiggleFlashing,
} from "NavBar/stateHooks";
import { everyoneButtonHeight } from "NavBar/styleConstants";

import { navColors } from "UI/colors";

// import useHeight from "Utilities/Hooks/useHeight";
import dynHeight from "Utilities/interpolations/dynHeight";
import dynMinHeight from "Utilities/interpolations/dynMinHeight";
import useMutationObserver2 from "Utilities/useMutationObserver/useMutationObserver2";

import FloatingButtonPanel from "./FloatingButtonPanel";
import NetworkGroups from "./Groups/NetworkGroups";
import NoSearchMatches from "./Groups/NoSearchMatches";
import {
  useDebouncedSearchValue,
  useSearchContainerRef,
} from "./Groups/groupsCommunicationStore";
import SettingsContent from "./SettingsContent";

const GroupCommunication = () => {
  const [
    { canSeeSuperUnolytics, canSeeUnolytics, canSeeLibrary, canManageTTJ },
  ] = usePermissions();
  const [{ hasAllContactsNoteSending }] = useNetworkMetadata();
  const debouncedSearchValue = useDebouncedSearchValue();
  const isSearching = debouncedSearchValue.length > 0;
  const searchContainerRef = useSearchContainerRef();
  const scrollRef = useRef();
  const groupCommunicationRef = useGroupCommunicationRef();
  const groupCommunicationSettingsRef = useGroupCommunicationSettingsRef();
  const isSquiggleFlashing = useIsGroupCommunicationSquiggleFlashing();
  const isSettingsFlashing = useIsGroupCommunicationSettingsFlashing();
  const isEveryoneLinkFlashing = useIsEveryoneLinkFlashing();
  const everyoneLinkRef = useEveryoneLinkRef();

  useTopGroups(); // prefetch top groups data

  const numNavLinks =
    /* always show My Notes, Latest, & Everyone */ 3 +
    Number(Boolean(canSeeLibrary)) +
    Number(Boolean(canSeeUnolytics)) +
    Number(Boolean(canSeeSuperUnolytics));

  const [minHeight, setMinHeight] = useState(0);
  const wasSearchingRef = useRef(false);
  const observerRef = useRef();

  const timeoutRef = useRef();
  useEffect(() => {
    if (isSearching && !wasSearchingRef.current) {
      // search just started — lock the height so the area doesn't jump
      const el = searchContainerRef.current;
      if (el) {
        setMinHeight(el.clientHeight);

        // ratchet upward: if results grow the area, raise the floor so it never shrinks mid-search
        observerRef.current = new ResizeObserver(([entry]) => {
          setMinHeight((prev) => Math.max(prev, entry.target.clientHeight));
        });
        observerRef.current.observe(el);
      }
    } else if (!isSearching && wasSearchingRef.current) {
      // search just ended — wait for subscribed groups to animate back in before collapsing
      timeoutRef.current = setTimeout(() => {
        setMinHeight(0);
      }, 400);
    }

    wasSearchingRef.current = isSearching;

    return () => {
      clearTimeout(timeoutRef.current);
      observerRef.current?.disconnect();
    };
  }, [isSearching, searchContainerRef]);

  useMutationObserver2({
    targetSelector: "[data-cy='NavBar_Groups']",
    options: { childList: true, subtree: true },
    handleMutation: (mutation) => {
      if (!isSearching) return;

      setTimeout(() => {
        const parent = mutation.target?.parentElement;
        if (!(parent && parent.dataset.cy === "NavBar_Groups")) {
          return;
        }

        const noMatchesEl = document.querySelector(
          "[data-cy='NavBar_NoSearchMatches']"
        );
        const scrollTarget = noMatchesEl ?? parent;
        scrollTarget.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 400);
    },
  });

  const floatingButtonPanelRef = useRef();
  const floatingButtonPanelHeight =
    floatingButtonPanelRef.current?.clientHeight;
  const spaceForFloatingPanel =
    typeof floatingButtonPanelHeight === "number"
      ? /* subtract the NavSection flex-gap for a more snug fit */
        floatingButtonPanelHeight - 8
      : /* compensate a bit for the discounting of padding */
        everyoneButtonHeight + 8;

  return (
    <NavSectionWrapper>
      <NavSection
        label="Group Communication"
        icon="fa-comments-alt far"
        color={navColors.groups}
        data-cy="NavBar_GroupCommunication"
        navSectionKey={navSectionKeys.groups}
        SettingsContent={canManageTTJ ? SettingsContent : undefined}
        scrollRef={scrollRef}
        headerTextRef={groupCommunicationRef}
        isSquiggleFlashing={isSquiggleFlashing}
        settingsButtonRef={groupCommunicationSettingsRef}
        isSettingsFlashing={isSettingsFlashing}
        isFirst
        defaultRoute="/mynotes"
      >
        <NavLinksWrapper
          minWidth={
            numNavLinks === 4
              ? /* force 2 rows of 2, instead of a row of 3 and a row of 1 */ 110
              : 80
          }
        >
          {/* My Notes is available to all users all of the time */}
          <NavLink
            navSectionKey={navSectionKeys.groups}
            to="mynotes"
            data-cy="NavBar_MyNotes"
            icon="fa-duotone fa-solid fa-mailbox-flag-up fa-swap-opacity"
          >
            My Notes
          </NavLink>
          {/* The Latest/Newsfeed is available to all users all of the time */}
          <NavLink
            navSectionKey={navSectionKeys.groups}
            to="latestactivity"
            data-cy="NavBar_TheLatest"
            icon="fa-duotone fa-solid fa-newspaper fa-swap-opacity"
          >
            Latest
          </NavLink>
          {canSeeLibrary && (
            <NavLink
              navSectionKey={navSectionKeys.groups}
              to="library"
              data-cy="NavBar_Library"
              icon="fa-duotone fa-solid fa-book-open-cover fa-swap-opacity"
            >
              Library
            </NavLink>
          )}
          {canSeeUnolytics && (
            <NavLink
              navSectionKey={navSectionKeys.groups}
              to="unolytics"
              data-cy="NavBar_Unolytics"
              icon="fa-duotone fa-solid fa-chart-line-up"
            >
              Unolytics
            </NavLink>
          )}
          {canSeeSuperUnolytics && (
            <NavLink
              navSectionKey={navSectionKeys.groups}
              to="superunolytics"
              data-cy="NavBar_SuperUnolytics"
              icon="analytics fad"
            >
              Diolytics
            </NavLink>
          )}
          <NavLink
            navSectionKey={navSectionKeys.groups}
            to="everyone"
            data-cy="NavBar_Everyone"
            icon={hasAllContactsNoteSending ? "mail-bulk fad" : "users fad"}
            isFlashing={isEveryoneLinkFlashing}
            ref={everyoneLinkRef}
          >
            Everyone
          </NavLink>
        </NavLinksWrapper>
        <NavSectionDivider />
        <NoSearchMatches />
        <GroupsAndAllGroupsWrapper data-cy="NavBar_Groups">
          <SLinkListRowsWrapper $minHeight={minHeight} ref={searchContainerRef}>
            {/* Groups are available to all users all of the time */}
            <NetworkGroups />
          </SLinkListRowsWrapper>
        </GroupsAndAllGroupsWrapper>
        <SpaceForFloatingButtonPanel $height={spaceForFloatingPanel} />
      </NavSection>
      <FloatingButtonPanel ref={floatingButtonPanelRef} />
    </NavSectionWrapper>
  );
};

const SpaceForFloatingButtonPanel = styled.div`
  ${dynHeight}
`;

const NavSectionWrapper = styled.div`
  position: relative;
`;

const SLinkListRowsWrapper = styled(LinkListRowsWrapper)`
  gap: 0;
  ${dynMinHeight}
  transition: min-height 300ms;
`;

const GroupsAndAllGroupsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  // not "magic," but hand-adjusted
  scroll-margin: 40px;
`;

export default GroupCommunication;
